import useTranslation from 'next-translate/useTranslation'
import { LocaleEnum, SalaryStats } from 'generated/apolloComponents'
import Trans from 'next-translate/Trans'
import dynamic from 'next/dynamic'
import SalaryChartItemWrapper from '../molecules/SalaryChartItemWrapper'
import SalaryTableRow from '../molecules/SalaryTableRow'
import Breadcrumbs from '../atoms/Breadcrumbs'

interface IProps {
  statsData: SalaryStats
}

const SalaryReportTemplate = ({ statsData }: IProps) => {
  const { t, lang } = useTranslation('it_report')
  const DynamicRelatedJobs = dynamic(
    () => import('../molecules/SalaryStatRelatedJobsList'),
    { ssr: false }
  )
  return (
    <main className='bg-gray-bg py-4'>
      <Breadcrumbs
        data={[
          { title: t('salary-breadcrumbs-all'), url: '/it-report' },
          {
            title: t('salary-breadcrumbs-salary-index'),
            url: '/it-report/salaries',
          },
          { title: statsData.position },
        ]}
        className='container-medium'
      />
      <div className='container container-medium h-full my-10'>
        <h1 className='mt-12 md:mt-20 text-4.5xl'>{statsData.title}</h1>
        <p className='text-sm mt-4 font-thin'>
          {t('salary-update', { update: statsData.lastUpdate })}
        </p>
        <div
          className='mt-4 text-xl leading-8 list-disc'
          dangerouslySetInnerHTML={{ __html: statsData.intro as string }}
        />
        <h2 className='mt-16 text-3xl'>{statsData.salaryBracketsTitle}</h2>
        <p className='pt-4'>{t('salary-brackets-intro')}</p>
        <div className='flex flex-col md:flex-row gap-12 mt-10'>
          <SalaryChartItemWrapper
            statsData={statsData}
            type='salaryBrackets'
            seniority='all'
            employmentType='employment'
            className='bg-black'
          />
          <SalaryChartItemWrapper
            statsData={statsData}
            type='salaryBrackets'
            seniority='all'
            employmentType='b2b'
            className='bg-black'
          />
        </div>
        <table className='table-auto md:rounded-2xl bg-white mt-16 -mx-6 w-screen md:w-full md:mx-0'>
          <caption className='pb-6 text-2xl'>
            {t('salary-brackets-table')}
          </caption>
          <thead>
            <tr className='border-b-1 border-gray-200'>
              <th className='text-center p-4'>{t('salary-table-position')}</th>
              <th className='text-center p-4'>
                {t('salary-table-employment')}
              </th>
              <th className='text-center p-4'>{t('salary-table-b2b')}</th>
            </tr>
          </thead>
          <tbody>
            <SalaryTableRow
              statsData={statsData}
              type='salaryBrackets'
              seniority='junior'
            />
            <SalaryTableRow
              statsData={statsData}
              type='salaryBrackets'
              seniority='medium'
            />
            <SalaryTableRow
              statsData={statsData}
              type='salaryBrackets'
              seniority='senior'
            />
          </tbody>
        </table>
        <div className='pt-12 font-thin'>{t('salary-brackets-data')}</div>

        {statsData.reported && (
          <>
            <h2 className='mt-10 text-3xl'>{statsData.reportedTitle}</h2>
            <p className='pt-4'>{t('salary-reported-intro')}</p>
            <div className='flex flex-col md:flex-row gap-12 mt-10'>
              <SalaryChartItemWrapper
                statsData={statsData}
                type='reported'
                seniority='all'
                employmentType='employment'
                className='bg-purple-home'
              />
              <SalaryChartItemWrapper
                statsData={statsData}
                type='reported'
                seniority='all'
                employmentType='b2b'
                className='bg-purple-home'
              />
            </div>
            <table className='table-auto w-full rounded-2xl bg-white mt-16'>
              <caption className='pb-6 text-2xl'>
                {t('salary-reported-table')}
              </caption>
              <thead>
                <tr className='border-b-1 border-gray-200'>
                  <th className='text-center p-4'>
                    {t('salary-table-position')}
                  </th>
                  <th className='text-center p-4'>
                    {t('salary-table-employment')}
                  </th>
                  <th className='text-center p-4'>{t('salary-table-b2b')}</th>
                </tr>
              </thead>
              <tbody>
                <SalaryTableRow
                  statsData={statsData}
                  type='reported'
                  seniority='junior'
                />
                <SalaryTableRow
                  statsData={statsData}
                  type='reported'
                  seniority='medium'
                />
                <SalaryTableRow
                  statsData={statsData}
                  type='reported'
                  seniority='senior'
                />
              </tbody>
            </table>
            <div className='pt-12 font-thin'>
              <Trans
                i18nKey='it_report:salary-reported-data'
                components={{
                  link: <a href='/it-report/2025' className='text-purple' />,
                }}
              />
            </div>
          </>
        )}

        <div className='pt-12'>
          <DynamicRelatedJobs
            slug={statsData.id}
            locale={lang as LocaleEnum}
            title={t('salary-jobs', { position: statsData.position })}
          />
        </div>
      </div>
    </main>
  )
}

export default SalaryReportTemplate
